import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { createPortal } from "react-dom";
import { GitBranch, List } from "lucide-react";

/**
 * Matching as a list (default) or a wire board.
 *
 * List: each key sits on the left; the value on the right starts blank. Tap
 * the blank to pick from a popup. Wire board: stems on the left, matches on
 * the right, curves between them. Same 1:1 answers either way.
 *
 * Left and right ids are not the answer. The server sends them as two lists,
 * and a right id is never the left's id. Green, red, and the key line come
 * from marks and keyPairs, which are absent until the attempt is turned in.
 */

type Side = "left" | "right";
type Armed = { side: Side; id: number };

type Wire = {
  key: string;
  d: string;
  tone: "plain" | "ok" | "bad" | "key" | "drag";
  color?: string;
};

/** Soft, distinct pair tints. Unlinked rights stay uncolored so this is not a key. */
const PAIR_COLORS = [
  "#2a9d8f",
  "#e07a5f",
  "#6d5cae",
  "#d4a017",
  "#3d7ea6",
  "#c45c99",
  "#5b8c3e",
  "#d46a3a",
  "#4a90c8",
  "#8e6bb0",
];

type DragPreview = {
  side: Side;
  id: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  over: Armed | null;
};

type DragSession = {
  pointerId: number;
  side: Side;
  id: number;
  startX: number;
  startY: number;
  x1: number;
  y1: number;
  moved: boolean;
};

export type MatchSide = { id: number; text: string };

type Props = {
  lefts: MatchSide[];
  rights: MatchSide[];
  value?: Record<string, number>;
  onChange?: (pairs: Record<string, number>) => void;
  disabled?: boolean;
  graded?: boolean;
  /** Left id → whether that match was right. Only after submit, and only when incorrect answers are shown. */
  marks?: Record<string, boolean>;
  /** Left id → correct right id. Only after submit, and only when the answer key is on. */
  keyPairs?: Record<string, number>;
  seed?: string;
};

export default function StudentMatchingBoard({
  lefts,
  rights: rightItems,
  value = {},
  onChange,
  disabled,
  graded,
  marks,
  keyPairs,
  seed,
}: Props) {
  const boardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragSession | null>(null);
  const skipClickRef = useRef(false);
  const [mode, setMode] = useState<"list" | "wires">("list");
  const [armed, setArmed] = useState<Armed | null>(null);
  const [drag, setDrag] = useState<DragPreview | null>(null);
  const [wires, setWires] = useState<Wire[]>([]);
  const [status, setStatus] = useState("");

  const rights = useMemo(
    () => stableShuffle(rightItems, seed || lefts.map((row) => row.id).join("-")),
    [rightItems, lefts, seed],
  );
  const colorByLeftId = new Map(lefts.map((row, index) => [row.id, PAIR_COLORS[index % PAIR_COLORS.length]]));
  const showKey = keyPairs != null;

  useEffect(() => {
    if (disabled) {
      setArmed(null);
      setDrag(null);
      dragRef.current = null;
    }
  }, [disabled]);

  useEffect(() => {
    setArmed(null);
    setDrag(null);
    dragRef.current = null;
  }, [mode]);

  useLayoutEffect(() => {
    if (mode !== "wires") {
      setWires([]);
      return;
    }
    const board = boardRef.current;
    if (!board) return;

    const measure = () => {
      const root = board.getBoundingClientRect();
      const next: Wire[] = [];
      for (const left of lefts) {
        const picked = value[String(left.id)];
        if (picked != null) {
          const path = portPath(board, root, "left", left.id, "right", picked);
          if (path) {
            const ok = marks?.[String(left.id)];
            let tone: Wire["tone"] = "plain";
            if (graded && ok != null) tone = ok ? "ok" : "bad";
            next.push({
              key: `link-${left.id}-${picked}`,
              d: path,
              tone,
              color: tone === "plain" ? colorByLeftId.get(left.id) : undefined,
            });
          }
        }
        const keyId = keyPairs?.[String(left.id)];
        if (keyId != null && (picked == null || picked !== Number(keyId))) {
          const path = portPath(board, root, "left", left.id, "right", Number(keyId));
          if (path) next.push({ key: `key-${left.id}`, d: path, tone: "key" });
        }
      }
      setWires((prev) => (sameWires(prev, next) ? prev : next));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(board);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [lefts, rights, value, graded, marks, keyPairs, armed, mode]);

  const connect = (leftId: number, rightId: number) => {
    if (disabled || !onChange) return;
    const next = { ...value };
    if (next[String(leftId)] === rightId) {
      delete next[String(leftId)];
      onChange(next);
      const left = lefts.find((row) => row.id === leftId);
      const right = rights.find((row) => row.id === rightId);
      setStatus(`Unlinked ${label(left?.text)} from ${label(right?.text)}`);
      return;
    }
    for (const [from, to] of Object.entries(next)) {
      if (Number(to) === rightId) delete next[from];
    }
    next[String(leftId)] = rightId;
    onChange(next);
    const left = lefts.find((row) => row.id === leftId);
    const right = rights.find((row) => row.id === rightId);
    setStatus(`Linked ${label(left?.text)} to ${label(right?.text)}`);
  };

  const tapLeft = (id: number) => {
    if (disabled) return;
    if (armed?.side === "left" && armed.id === id) {
      setArmed(null);
      return;
    }
    if (armed?.side === "right") {
      connect(id, armed.id);
      setArmed(null);
      return;
    }
    setArmed({ side: "left", id });
  };

  const tapRight = (id: number) => {
    if (disabled) return;
    if (armed?.side === "right" && armed.id === id) {
      setArmed(null);
      return;
    }
    if (armed?.side === "left") {
      connect(armed.id, id);
      setArmed(null);
      return;
    }
    setArmed({ side: "right", id });
  };

  const tap = (side: Side, id: number) => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    if (side === "left") tapLeft(id);
    else tapRight(id);
  };

  const beginDrag = (event: PointerEvent<HTMLButtonElement>, side: Side, id: number) => {
    if (disabled || event.button !== 0) return;
    const board = boardRef.current;
    if (!board) return;
    const root = board.getBoundingClientRect();
    const origin = portCenter(board, root, side, id) || {
      x: event.clientX - root.left,
      y: event.clientY - root.top,
    };
    dragRef.current = {
      pointerId: event.pointerId,
      side,
      id,
      startX: event.clientX,
      startY: event.clientY,
      x1: origin.x,
      y1: origin.y,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const session = dragRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    const dist = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
    if (!session.moved && dist < 8) return;
    session.moved = true;
    skipClickRef.current = true;
    const board = boardRef.current;
    if (!board) return;
    const root = board.getBoundingClientRect();
    const over = hitNode(board, event.clientX, event.clientY);
    const drop = over && over.side !== session.side ? over : null;
    setArmed({ side: session.side, id: session.id });
    setDrag({
      side: session.side,
      id: session.id,
      x1: session.x1,
      y1: session.y1,
      x2: event.clientX - root.left,
      y2: event.clientY - root.top,
      over: drop,
    });
  };

  const endDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const session = dragRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
    if (!session.moved) {
      setDrag(null);
      return;
    }
    const board = boardRef.current;
    const over = hitNode(board, event.clientX, event.clientY);
    setDrag(null);
    if (over && over.side !== session.side) {
      const leftId = session.side === "left" ? session.id : over.id;
      const rightId = session.side === "right" ? session.id : over.id;
      connect(leftId, rightId);
    }
    setArmed(null);
  };

  if (!lefts.length) return null;

  const usedRights = new Set(Object.values(value).map(Number));
  const density = lefts.length >= 6 ? "many" : lefts.length >= 4 ? "some" : "few";
  const dragColor = drag
    ? drag.side === "left"
      ? colorByLeftId.get(drag.id)
      : colorByLeftId.get(Number(Object.entries(value).find(([, to]) => Number(to) === drag.id)?.[0]))
    : undefined;

  const listHint = disabled
    ? showKey
      ? graded
        ? "Green is right. Red is yours; the key is under your match."
        : "The key is under your match."
      : graded
        ? "Green is right. Red is yours."
        : "Each item on the left has its own match."
    : "Tap a blank to pick its match. Each match can only be used once.";
  const wireHint = disabled
    ? showKey
      ? graded
        ? "Green is right. Red is yours; the dashed line is the key."
        : "The dashed line is the key."
      : graded
        ? "Green is right. Red is yours."
        : "Each item on the left has its own match."
    : drag
      ? drag.over
        ? "Release to connect."
        : "Drop on a match on the other side."
      : armed
        ? "Now tap or drag to its match. A match can only be used once."
        : "Tap two items, or drag a wire from one to the other.";

  return (
    <div className={`student-matchboard student-matchboard--${density}`}>
      <div className="student-matchboard__bar">
        <p className="student-matchboard__hint">{mode === "list" ? listHint : wireHint}</p>
        <div className="student-viewtabs" role="tablist" aria-label="Matching view">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "list"}
            className={`student-viewtab${mode === "list" ? " is-on" : ""}`}
            onClick={() => setMode("list")}
          >
            <List size={13} aria-hidden />
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "wires"}
            className={`student-viewtab${mode === "wires" ? " is-on" : ""}`}
            onClick={() => setMode("wires")}
          >
            <GitBranch size={13} aria-hidden />
            Wires
          </button>
        </div>
      </div>
      {mode === "list" ? (
        <MatchList
          lefts={lefts}
          rights={rights}
          value={value}
          disabled={disabled}
          graded={graded}
          marks={marks}
          keyPairs={keyPairs}
          connect={connect}
        />
      ) : (
      <div className={`student-matchboard__stage${drag ? " is-dragging" : ""}`} ref={boardRef}>
        <svg className="student-matchboard__wires" aria-hidden>
          {wires.map((wire) => (
            <g key={wire.key}>
              <path d={wire.d} className="student-matchboard__wire student-matchboard__wire--halo" />
              <path
                d={wire.d}
                className={`student-matchboard__wire student-matchboard__wire--${wire.tone}`}
                style={wire.color ? { stroke: wire.color } : undefined}
              />
            </g>
          ))}
          {drag ? (
            <g>
              <path
                d={curve(drag.x1, drag.y1, drag.x2, drag.y2)}
                className="student-matchboard__wire student-matchboard__wire--halo"
              />
              <path
                d={curve(drag.x1, drag.y1, drag.x2, drag.y2)}
                className="student-matchboard__wire student-matchboard__wire--drag"
                style={dragColor ? { stroke: dragColor } : undefined}
              />
            </g>
          ) : null}
        </svg>

        <div className="student-matchboard__col">
          {lefts.map((row) => {
            const picked = value[String(row.id)];
            const linked = picked != null;
            const ok = marks?.[String(row.id)];
            const mark = graded && linked && ok != null ? (ok ? "ok" : "bad") : "";
            return (
              <MatchNode
                key={`L-${row.id}`}
                side="left"
                id={row.id}
                text={row.text.trim() || "Item"}
                color={colorByLeftId.get(row.id)}
                armed={armed?.side === "left" && armed.id === row.id}
                linked={linked}
                drop={drag?.over?.side === "left" && drag.over.id === row.id}
                mark={mark}
                disabled={disabled}
                onClick={() => tap("left", row.id)}
                onPointerDown={(event) => beginDrag(event, "left", row.id)}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
            );
          })}
        </div>

        <div className="student-matchboard__col">
          {rights.map((pair) => {
            const linked = usedRights.has(pair.id);
            const owner = Object.entries(value).find(([, to]) => Number(to) === pair.id);
            const ownerId = owner ? Number(owner[0]) : null;
            const mark = graded && ownerId != null && marks?.[String(ownerId)] != null
              ? marks[String(ownerId)]
                ? "ok"
                : "bad"
              : "";
            const dropping = drag?.over?.side === "right" && drag.over.id === pair.id;
            return (
              <MatchNode
                key={`R-${pair.id}`}
                side="right"
                id={pair.id}
                text={pair.text.trim() || "Match"}
                color={ownerId != null ? colorByLeftId.get(ownerId) : dropping ? dragColor : undefined}
                armed={armed?.side === "right" && armed.id === pair.id}
                linked={linked}
                drop={dropping}
                mark={mark}
                disabled={disabled}
                onClick={() => tap("right", pair.id)}
                onPointerDown={(event) => beginDrag(event, "right", pair.id)}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
            );
          })}
        </div>
      </div>
      )}
      <span className="student-vh" aria-live="polite">
        {status}
      </span>
    </div>
  );
}

function MatchList({
  lefts,
  rights,
  value,
  disabled,
  graded,
  marks,
  keyPairs,
  connect,
}: {
  lefts: MatchSide[];
  rights: MatchSide[];
  value: Record<string, number>;
  disabled?: boolean;
  graded?: boolean;
  marks?: Record<string, boolean>;
  keyPairs?: Record<string, number>;
  connect: (leftId: number, rightId: number) => void;
}) {
  const [openFor, setOpenFor] = useState<number | null>(null);
  const usedRights = new Set(Object.values(value).map(Number));

  useEffect(() => {
    if (disabled) setOpenFor(null);
  }, [disabled]);

  const openLeft = lefts.find((row) => row.id === openFor) || null;
  const selectedId = openLeft ? value[String(openLeft.id)] : undefined;

  return (
    <>
      <div className="student-matchlist">
        {lefts.map((row) => {
          const picked = value[String(row.id)];
          const match = picked != null ? rights.find((side) => side.id === picked) : null;
          const linked = Boolean(match);
          const ok = marks?.[String(row.id)];
          const mark = graded && linked && ok != null ? (ok ? "ok" : "bad") : "";
          const keyId = keyPairs?.[String(row.id)];
          const keyText =
            keyId != null ? rights.find((side) => side.id === Number(keyId))?.text.trim() : "";
          return (
            <div key={row.id} className="student-matchlist__row">
              <div className="student-matchlist__key">
                <span className="student-matchlist__keytext">{row.text.trim() || "Item"}</span>
              </div>
              <div className="student-matchlist__pick">
                <button
                  type="button"
                  className={[
                    "student-matchlist__value",
                    linked ? "is-filled" : "is-blank",
                    mark ? `is-${mark}` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={disabled}
                  aria-haspopup="dialog"
                  aria-expanded={openFor === row.id}
                  aria-label={
                    linked
                      ? `Match for ${label(row.text)}: ${label(match?.text)}. Activate to change.`
                      : `Choose a match for ${label(row.text)}`
                  }
                  onClick={() => setOpenFor(row.id)}
                >
                  {linked ? match?.text.trim() || "Match" : "Choose…"}
                </button>
                {keyText && (picked == null || picked !== Number(keyId)) ? (
                  <span className="student-matchlist__answer">{keyText}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {openLeft ? (
        <MatchPicker
          title={openLeft.text.trim() || "Item"}
          options={rights}
          selectedId={selectedId}
          usedIds={usedRights}
          onPick={(rightId) => {
            connect(openLeft.id, rightId);
            setOpenFor(null);
          }}
          onClose={() => setOpenFor(null)}
        />
      ) : null}
    </>
  );
}

function MatchPicker({
  title,
  options,
  selectedId,
  usedIds,
  onPick,
  onClose,
}: {
  title: string;
  options: MatchSide[];
  selectedId?: number;
  usedIds: Set<number>;
  onPick: (rightId: number) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = panelRef.current;
    node?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const root = typeof document !== "undefined" ? document.querySelector(".acuity-student") || document.body : null;
  if (!root) return null;

  return createPortal(
    <div className="student-matchpick" onClick={onClose}>
      <div
        ref={panelRef}
        className="student-matchpick__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-matchpick-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="student-matchpick__kicker">Match for</p>
        <h3 id="student-matchpick-title" className="student-matchpick__title">
          {title}
        </h3>
        <div className="student-matchpick__options">
          {options.map((opt) => {
            const selected = selectedId === opt.id;
            const used = usedIds.has(opt.id) && !selected;
            return (
              <button
                key={opt.id}
                type="button"
                className={`student-matchpick__option${selected ? " is-on" : ""}${used ? " is-used" : ""}`}
                onClick={() => {
                  if (selected) onClose();
                  else onPick(opt.id);
                }}
              >
                <span>{opt.text.trim() || "Match"}</span>
                {used ? <span className="student-matchpick__tag">Used</span> : null}
              </button>
            );
          })}
        </div>
        {selectedId != null ? (
          <button type="button" className="student-text-link student-matchpick__clear" onClick={() => onPick(selectedId)}>
            Clear
          </button>
        ) : null}
      </div>
    </div>,
    root,
  );
}

function MatchNode({
  side,
  id,
  text,
  color,
  armed,
  linked,
  drop,
  mark,
  disabled,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: {
  side: Side;
  id: number;
  text: string;
  color?: string;
  armed: boolean;
  linked: boolean;
  drop: boolean;
  mark: string;
  disabled?: boolean;
  onClick: () => void;
  onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => void;
}) {
  const size = nodeSize(text);
  const port = (
    <span className="student-match-node__port" data-port={`${side}:${id}`} aria-hidden />
  );
  return (
    <button
      type="button"
      data-match-node
      data-side={side}
      data-id={id}
      className={[
        "student-match-node",
        `student-match-node--${side}`,
        `student-match-node--${size}`,
        color ? "has-tone" : "",
        armed ? "is-armed" : "",
        linked ? "is-linked" : "",
        drop ? "is-drop" : "",
        mark ? `is-${mark}` : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={color ? ({ "--match-c": color } as CSSProperties) : undefined}
      disabled={disabled}
      aria-pressed={linked || armed}
      aria-label={`${side === "left" ? "Item" : "Match"}: ${text}${linked ? ", linked" : ""}${armed ? ", selected" : ""}`}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {side === "right" ? port : null}
      <span className="student-match-node__text">{text}</span>
      {side === "left" ? port : null}
    </button>
  );
}

function curve(x1: number, y1: number, x2: number, y2: number) {
  const mid = Math.max(36, Math.abs(x2 - x1) * 0.42);
  const c1 = x1 <= x2 ? x1 + mid : x1 - mid;
  const c2 = x1 <= x2 ? x2 - mid : x2 + mid;
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${c1.toFixed(1)} ${y1.toFixed(1)}, ${c2.toFixed(1)} ${y2.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
}

function portCenter(board: HTMLElement, root: DOMRect, side: Side, id: number) {
  const el = board.querySelector(`[data-port="${side}:${id}"]`);
  if (!(el instanceof HTMLElement)) return null;
  const box = el.getBoundingClientRect();
  return { x: box.left + box.width / 2 - root.left, y: box.top + box.height / 2 - root.top };
}

function portPath(
  board: HTMLElement,
  root: DOMRect,
  fromSide: Side,
  fromId: number,
  toSide: Side,
  toId: number,
) {
  const a = portCenter(board, root, fromSide, fromId);
  const b = portCenter(board, root, toSide, toId);
  if (!a || !b) return "";
  return curve(a.x, a.y, b.x, b.y);
}

function hitNode(board: HTMLElement | null, x: number, y: number): Armed | null {
  if (!board) return null;
  const el = document.elementFromPoint(x, y);
  if (!(el instanceof Element) || !board.contains(el)) return null;
  const node = el.closest("[data-match-node]");
  if (!node) return null;
  const side = node.getAttribute("data-side");
  const id = Number(node.getAttribute("data-id"));
  if ((side !== "left" && side !== "right") || !Number.isFinite(id)) return null;
  return { side, id };
}

function nodeSize(text: string): "lg" | "md" | "sm" {
  const t = String(text || "").trim();
  const lines = t.split(/\r?\n/).length;
  const words = t.split(/\s+/).filter(Boolean).length;
  if (t.length > 80 || lines > 3 || words > 16) return "sm";
  if (t.length > 32 || lines > 1 || words > 7) return "md";
  return "lg";
}

function sameWires(a: Wire[], b: Wire[]) {
  if (a.length !== b.length) return false;
  return a.every((w, i) => w.key === b[i].key && w.d === b[i].d && w.tone === b[i].tone && w.color === b[i].color);
}

function label(text: string | undefined) {
  const t = String(text || "").trim() || "item";
  return t.length > 40 ? `${t.slice(0, 37)}…` : t;
}

/** Deterministic so the right column does not jump when the student answers. */
function stableShuffle<T extends { id: number }>(items: T[], seed: string): T[] {
  const out = [...items];
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  for (let i = out.length - 1; i > 0; i -= 1) {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    const j = (h >>> 0) % (i + 1);
    const swap = out[i];
    out[i] = out[j];
    out[j] = swap;
  }
  return out;
}
